import { useState, useEffect, useRef } from "react";
import { format, subDays } from "date-fns";
import { Search, X, BookOpen, Loader2, BookMarked } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDiarySearch, type DiarySearchEntry } from "@/lib/program-registration";

interface DiaryEntryPickerProps {
  /** Full linked entry — shown as a rich chip when available */
  linkedEntry: DiarySearchEntry | null;
  /**
   * ID of the linked entry when we only have the ID (e.g. editing a
   * pre-existing diary-linked event whose full object hasn't been fetched).
   * When set and linkedEntry is null, a minimal stub chip is shown instead
   * of the full search list so the teacher can still see and clear the link.
   */
  linkedEntryId?: number | null;
  /** Delivery date from the event — used as the fallback date label in stub chip */
  fallbackDate?: string;
  onSelect: (entry: DiarySearchEntry | null) => void;
  onClearById?: () => void;
}

function fmtSearchDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

function formatSlotLabel(slotKey: string): string {
  return slotKey
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DiaryEntryPicker({
  linkedEntry,
  linkedEntryId,
  fallbackDate,
  onSelect,
  onClearById,
}: DiaryEntryPickerProps) {
  const today = fmtSearchDate(new Date());
  const ninetyDaysAgo = fmtSearchDate(subDays(new Date(), 90));

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [startDate, setStartDate] = useState(ninetyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasAnyLink = !!linkedEntry || !!(linkedEntryId ?? null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedQ(q), 350);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [q]);

  const { data, isFetching, isError } = useDiarySearch({
    q: debouncedQ,
    startDate,
    endDate,
    enabled: !hasAnyLink,
  });

  const entries = data?.entries ?? [];

  // ── Rich linked chip (full object available) ─────────────────────────────
  if (linkedEntry) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <BookMarked className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-primary">
              Linked from diary — {format(new Date(linkedEntry.date + "T12:00:00"), "d MMM yyyy")}
            </p>
            {linkedEntry.courseName && (
              <p className="text-xs text-muted-foreground">{linkedEntry.courseName}</p>
            )}
            {linkedEntry.contentPreview && (
              <p className="text-xs text-foreground/70 mt-0.5 line-clamp-2">
                {linkedEntry.contentPreview}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground flex-shrink-0"
            onClick={() => onSelect(null)}
            title="Remove diary link"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  // ── Stub chip (ID only — editing a pre-existing link) ────────────────────
  if ((linkedEntryId ?? null) !== null) {
    const dateLabel = fallbackDate
      ? format(new Date(fallbackDate + "T12:00:00"), "d MMM yyyy")
      : "date unknown";
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <BookMarked className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-primary">
              Linked from diary — {dateLabel}
            </p>
            <p className="text-xs text-muted-foreground">Diary entry #{linkedEntryId}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground flex-shrink-0"
            onClick={onClearById ?? (() => onSelect(null))}
            title="Remove diary link"
            aria-label="Remove diary link"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  // ── Picker (search mode) ─────────────────────────────────────────────────
  return (
    <div className="space-y-2 rounded-lg border border-input bg-muted/20 p-3">
      {/* Date range filters */}
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <label className="text-xs text-muted-foreground mb-1 block">From</label>
          <Input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-xs text-muted-foreground mb-1 block">To</label>
          <Input
            type="date"
            value={endDate}
            min={startDate}
            max={today}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      </div>

      {/* Text search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search lesson content…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-8 h-8 text-sm"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Results */}
      {isFetching ? (
        <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Searching…
        </div>
      ) : isError ? (
        <p className="text-xs text-destructive py-3 text-center">
          Could not load diary entries. Please try again.
        </p>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 py-5 text-center">
          <BookOpen className="h-7 w-7 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">
            {debouncedQ
              ? "No diary entries match your search"
              : "No diary entries in this date range"}
          </p>
          {!debouncedQ && (
            <p className="text-xs text-muted-foreground/70">
              Write lessons in the Diary/Daybook and they will appear here
            </p>
          )}
        </div>
      ) : (
        <ScrollArea className="max-h-[38vh] sm:max-h-64">
          <div className="space-y-1 pr-1">
            {entries.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => onSelect(entry)}
                className="w-full text-left px-2.5 py-2 rounded-md hover:bg-background border border-transparent hover:border-input transition-colors group"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 text-center min-w-[2.5rem]">
                    <div className="text-xs font-semibold text-foreground">
                      {format(new Date(entry.date + "T12:00:00"), "d MMM")}
                    </div>
                    <div className="text-[10px] text-muted-foreground leading-tight">
                      {format(new Date(entry.date + "T12:00:00"), "yyyy")}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {entry.courseName && (
                        <span className="text-[11px] font-medium text-foreground">
                          {entry.courseName}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {formatSlotLabel(entry.slotKey)}
                      </span>
                    </div>
                    {entry.contentPreview ? (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                        {entry.contentPreview}
                      </p>
                    ) : entry.dayNotes ? (
                      <p className="text-xs text-muted-foreground/70 mt-0.5 line-clamp-1 italic">
                        Day note: {entry.dayNotes}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground/50 mt-0.5 italic">
                        No content
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
